/**
 * Which convention this organization works to, and noticing when FlowZa changes it.
 *
 * `organizations.workflow_standard` can move under a console that is already open — an admin sets it
 * in Settings (ADR 0087), or an operator sets it through `app_private.set_workflow_standard` — and
 * in neither case is THIS tab the one that did it. So a tenant's console finds out the way it finds
 * out about anything nobody told it: by asking. Founder decision D8 says every signed-in browser
 * applies the new theme and reloads itself within a minute of the change. The tab that DID do it
 * calls `refresh()` and does not wait.
 *
 * ## Why a poll and not a push
 *
 * There is no websocket in this product and no Realtime subscription on the console. Adding either
 * for a value that changes once in a tenant's lifetime would be a persistent connection per open
 * tab to carry, on average, zero messages. `GET /v1/organization/workflow` reads two columns with
 * `Cache-Control: no-store`; a poll of that once a minute, only while the tab is VISIBLE, is the
 * proportionate answer.
 *
 * ## Why it reloads rather than re-rendering
 *
 * The standard changes which composer the console opens, which palette it paints, and what a
 * generated subject looks like. Re-deriving all of that in place would mean every screen in the app
 * had to be correct about a value changing under it mid-render — including the composer, which is a
 * multi-step form somebody may be halfway through. A reload is one line and is correct everywhere,
 * and a sender mid-compose loses nothing, because the composer's state is the draft envelope on the
 * server rather than the form.
 *
 * The toast comes FIRST and the reload follows after a beat, so the page that vanishes has already
 * said why.
 *
 * ## The cache
 *
 * `localStorage`, beside the theme cache and with the same discipline (ADR 0063): every read and
 * write in try/catch, because the accessor itself throws in a private window rather than returning
 * null. It exists so the FIRST paint is the right palette — without it every reload of an
 * international tenant's console flashes FlowZa indigo until the fetch lands.
 *
 * A failed poll KEEPS the cache. A network blip must not repaint a tenant's console in the wrong
 * colours, and "we could not ask" is not "the answer changed".
 *
 * ## A 404 stops the clock
 *
 * A console can be talking to an API that predates `GET /v1/organization/workflow`: Pages lands
 * minutes before the Worker, and a failed promotion leaves it stale for as long as nobody looks
 * (CLAUDE.md §7, ADR 0109). That answer will not change until the promotion lands, and asking once
 * a minute anyway is 1,440 failed requests per open tab per day — measured on production before
 * this existed, in every tab, for as long as the gap lasted. So the FIRST 404 (`no_such_route`, or
 * a bare 404 from a Worker older than that code) stops the interval for the rest of the session.
 * The cache stands, and the `focus`/`visibilitychange` re-ask stays: a tab brought forward after
 * the promotion lands asks once and is back on the clock if the route now answers. Anything other
 * than a 404 — a 5xx, a dropped connection — is a blip and keeps polling.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError, api, apiConfigured } from '../api/client'

export type WorkflowProfile = {
  readonly workflow_standard: 'flowza_sign' | 'international'
  readonly workflow_standard_changed_at: string | null
  readonly theme_palette: 'flowza' | 'docusign'
  readonly composer: 'classic' | 'international'
  readonly subject_prefix: string | null
}

/**
 * What a console assumes before it has been told.
 *
 * `flowza_sign` is everything built to date, so a browser that cannot reach the API gets the flow
 * that has always existed rather than a composer it was never switched to. Being wrong in the other
 * direction would show a tenant a product they did not buy.
 */
export const DEFAULT_WORKFLOW: WorkflowProfile = {
  workflow_standard: 'flowza_sign',
  workflow_standard_changed_at: null,
  theme_palette: 'flowza',
  composer: 'classic',
  subject_prefix: null,
}

const CACHE_KEY = 'flowza.workflow'

/** Sixty seconds, and only while the tab is visible. See the header. */
const POLL_MS = 60_000

/** Long enough to read one line, short enough not to feel like a hang. */
const RELOAD_DELAY_MS = 1500

function isProfile(value: unknown): value is WorkflowProfile {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return v['workflow_standard'] === 'flowza_sign' || v['workflow_standard'] === 'international'
}

export function cachedWorkflow(): WorkflowProfile {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (raw === null) return DEFAULT_WORKFLOW
    const parsed: unknown = JSON.parse(raw)
    return isProfile(parsed) ? parsed : DEFAULT_WORKFLOW
  } catch {
    // A private window, blocked site data, or a cache somebody hand-edited. The default is a
    // correct console, so there is nothing to report.
    return DEFAULT_WORKFLOW
  }
}

function cacheWorkflow(profile: WorkflowProfile): void {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(profile))
  } catch {
    // A browser that will not store it still renders correctly, one fetch later.
  }
}

export type WorkflowState = {
  readonly profile: WorkflowProfile
  /** Set for the moment between noticing a change and reloading. The shell shows it as a toast. */
  readonly switching: boolean
  /**
   * Ask NOW rather than waiting out the poll.
   *
   * For the one case where the console already knows something moved: the admin who just saved a
   * new standard in Settings (ADR 0087 — the choice came back to the tenant on 2026-09-13). Sixty
   * seconds of the old palette after clicking is indistinguishable from a save that did not work,
   * and the alternative — repainting from the PATCH response — would be a second path to the same
   * state that could disagree with this one.
   */
  readonly refresh: () => void
}

const WorkflowContext = createContext<WorkflowState>({
  profile: DEFAULT_WORKFLOW,
  switching: false,
  refresh: () => {},
})

export function useWorkflow(): WorkflowState {
  return useContext(WorkflowContext)
}

/**
 * The poll, the cache and the reload.
 *
 * `reload` is injected so a test can observe it without a jsdom navigation, and defaulted to the
 * real thing so no caller has to know it exists.
 */
export function WorkflowProvider({
  children,
  reload = () => window.location.reload(),
}: {
  readonly children: ReactNode
  readonly reload?: () => void
}) {
  const [profile, setProfile] = useState<WorkflowProfile>(cachedWorkflow)
  const [switching, setSwitching] = useState(false)
  // A ref and not state: the poll compares against it, and comparing against state would capture
  // the value from the render the interval was created in and report every poll as a change.
  const current = useRef(profile)
  // Also a ref, and this one is load-bearing. The default `reload` above is a NEW function on
  // every render, and with it in `applyProfile`'s dependencies the whole chain — `applyProfile`,
  // `poll`, the effect below — was recreated each render. The effect polls on mount, the poll's
  // `setProfile` renders, the render recreates the effect, the effect polls again: one request per
  // round trip, forever, until the API answered 429. Measured on production 2026-09-12: 38 GETs of
  // `/v1/organization/workflow` in a row from one settings screen. Reading the latest `reload`
  // through a ref keeps the callbacks stable while still honouring a caller who swaps it.
  const reloadRef = useRef(reload)
  useEffect(() => {
    reloadRef.current = reload
  }, [reload])

  const applyProfile = useCallback((next: WorkflowProfile) => {
    const changed = next.workflow_standard !== current.current.workflow_standard
    current.current = next
    cacheWorkflow(next)
    setProfile(next)
    if (!changed) return
    // The toast first, the reload after a beat: the page that is about to vanish says why.
    setSwitching(true)
    window.setTimeout(() => reloadRef.current(), RELOAD_DELAY_MS)
  }, [])

  // True once the deployment has said it does not serve the route. A ref, not state: nothing
  // renders differently, and the interval reads it on every tick.
  const routeMissing = useRef(false)

  const poll = useCallback(() => {
    if (!apiConfigured()) return
    void api<WorkflowProfile>('/v1/organization/workflow')
      .then((next) => {
        routeMissing.current = false
        if (isProfile(next)) applyProfile(next)
      })
      .catch((error: unknown) => {
        // A blip is not an answer. The cache stands, and the next poll asks again — unless the
        // deployment has said it does not HAVE the route, in which case the next poll would be the
        // same 404 and so would the 1,439 after it. See the header.
        if (error instanceof ApiError && (error.code === 'no_such_route' || error.status === 404)) {
          routeMissing.current = true
        }
      })
  }, [applyProfile])

  useEffect(() => {
    poll()
    const timer = window.setInterval(() => {
      // `hidden` rather than `visibilityState !== 'visible'`: a background tab costs the tenant a
      // request a minute for a console nobody is looking at, and it will ask the moment it is
      // brought forward by the listener below.
      if (!document.hidden && !routeMissing.current) poll()
    }, POLL_MS)
    const onWake = () => {
      if (!document.hidden) poll()
    }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('focus', onWake)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('focus', onWake)
    }
  }, [poll])

  return (
    <WorkflowContext.Provider value={{ profile, switching, refresh: poll }}>
      {children}
    </WorkflowContext.Provider>
  )
}
